import React, { useEffect, useState, useRef, useCallback } from "react";
import API from "../api/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  BadgeCheck, MapPin, ChevronRight, ShoppingCart, Heart,
  Clock, Star, Store, UtensilsCrossed, Percent, Navigation, Loader2, Gavel,
  Sparkles, Eye,
} from "lucide-react";
import MobileBottomBar from "../components/MobileBottomBar";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { toast } from "react-toastify";
import CartToast from "../components/CartToast";
import { isSedeOpen } from "../utils/sedeStatus";
import { useSearch } from "../context/SearchContext";
import { requestPermissionAndToken, listenForegroundMessages } from "../firebase";
import { getImageUrl } from "../utils/getImageUrl";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import CartQtyControl from "../components/CartQtyControl";

function normalize(obj) {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj.map(normalize);
  return { ...obj, _id: obj._id ?? obj.id };
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Verified companies first, shuffle within each group
function sortVerifiedFirst(arr) {
  return [...shuffle(arr.filter((c) => c.verified)), ...shuffle(arr.filter((c) => !c.verified))];
}

// â"€â"€â"€ Sub-components â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function SectionHeader({ title, onSeeAll, seeAllLabel = "Ver todos", count }) {
  return (
    <div className="flex items-center justify-between mb-4 px-4">
      <div className="flex items-center gap-2.5">
        <div className="w-1 h-5 bg-supernova-500 rounded-full shrink-0" />
        <h2 className="text-base font-black text-gray-900 tracking-tight">{title}</h2>
        {count != null && (
          <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </div>
      {onSeeAll && (
        <button
          onClick={onSeeAll}
          className="flex items-center gap-1 text-xs font-bold text-supernova-700 hover:text-supernova-800 transition-colors"
        >
          {seeAllLabel} <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

// â"€â"€ Skeleton card â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function SkeletonCard({ wide = false }) {
  return (
    <div className={`flex-shrink-0 ${wide ? "w-56" : "w-40"} rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden animate-pulse`}>
      <div className={`${wide ? "h-40" : "h-24"} bg-gray-100`} />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-gray-100 rounded-full w-4/5" />
        <div className="h-2.5 bg-gray-100 rounded-full w-3/5" />
        <div className="h-2 bg-gray-100 rounded-full w-2/5" />
      </div>
    </div>
  );
}

// â"€â"€ Restaurant card (Rappi-style, tall image with overlay) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function RestaurantCard({ company, onClick, index, showTitle = true }) {
  const isOpen = Array.isArray(company.sedes) && company.sedes.some((s) => isSedeOpen(s).open);
  return (
    <motion.div
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      whileHover={{ y: -3, boxShadow: "0 12px 32px rgba(0,0,0,0.12)" }}
      className="cursor-pointer w-56 flex-shrink-0 rounded-2xl bg-white shadow-sm overflow-hidden"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
    >
      {/* Cover image */}
      <div className="relative h-40 bg-gray-100 overflow-hidden">
        {company.logo
          ? <img
              src={getImageUrl(company.logo)}
              alt={company.name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          : <div className="w-full h-full bg-gradient-to-br from-supernova-50 to-supernova-100 flex items-center justify-center">
              <UtensilsCrossed size={36} className="text-supernova-300" />
            </div>
        }

        {/* Dim overlay when closed */}
        {!isOpen && <div className="absolute inset-0 bg-black/30" />}

        {/* Open/closed chip */}
        <div className={`absolute top-3 right-3 flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md
          ${isOpen ? "bg-green-500 text-white" : "bg-white/90 text-gray-600"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-white" : "bg-gray-400"}`} />
          {isOpen ? "Abierto" : "Cerrado"}
        </div>

        {/* Category pill top-left */}
        {showTitle && company.category?.name && (
          <div className="absolute top-3 left-3 bg-black/40 backdrop-blur-sm text-white text-[9px] font-semibold px-2 py-0.5 rounded-full">
            {company.category.name}
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* Info below image */}
      <div className="px-3 pt-3 pb-3">
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="font-black text-sm text-gray-900 truncate">{company.name}</p>
              {company.verified && <BadgeCheck size={13} className="text-blue-500 shrink-0" />}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-1.5">
          {/* Rating placeholder */}
          <div className="flex items-center gap-0.5">
            <Star size={11} className="text-yellow-400 fill-yellow-400" />
            <span className="text-[11px] font-bold text-gray-700">
              {company.rating ? Number(company.rating).toFixed(1) : "Nuevo"}
            </span>
          </div>

          {company.nearestDistanceKm != null && (
            <span className="flex items-center gap-0.5 text-[10px] text-gray-400 font-medium">
              <MapPin size={9} /> {company.nearestDistanceKm} km
            </span>
          )}

          {!isOpen && (
            <span className="flex items-center gap-0.5 text-[10px] text-orange-500 font-semibold">
              <Clock size={9} /> Cerrado
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// â"€â"€ Store card (compact, for "otras tiendas") â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function StoreCard({ company, onClick, index, showTitle = true }) {
  const isOpen = Array.isArray(company.sedes) && company.sedes.some((s) => isSedeOpen(s).open);
  return (
    <motion.div
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="cursor-pointer w-40 flex-shrink-0 rounded-2xl bg-white overflow-hidden"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
    >
      <div className="relative h-24 bg-gray-100 overflow-hidden">
        {company.logo
          ? <img src={getImageUrl(company.logo)} alt={company.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-supernova-50 flex items-center justify-center">
              <Store size={24} className="text-supernova-300" />
            </div>
        }
        {!isOpen && <div className="absolute inset-0 bg-black/20" />}
        <span className={`absolute top-2 right-2 w-2 h-2 rounded-full border-2 border-white shadow
          ${isOpen ? "bg-green-500" : "bg-gray-400"}`}
        />
      </div>
      <div className="px-2.5 pt-2 pb-2.5">
        <div className="flex items-center gap-0.5 mb-0.5">
          <p className="font-bold text-[11px] text-gray-900 truncate">{company.name}</p>
          {company.verified && <BadgeCheck size={9} className="text-blue-500 shrink-0" />}
        </div>
        <p className="text-[10px] text-gray-400 truncate">{company.category?.name}</p>
        {company.nearestDistanceKm != null && (
          <p className="flex items-center gap-0.5 text-[10px] text-gray-400 mt-1">
            <MapPin size={8} /> {company.nearestDistanceKm} km
          </p>
        )}
      </div>
    </motion.div>
  );
}

// â"€â"€ Offer card â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function OfferCard({ product, onClick, showTitle = true, isOpen = true, onQuickView }) {
  const discount = Math.round(((product.price - product.offerPrice) / product.price) * 100);
  return (
    <motion.div
      onClick={onClick}
      whileHover={{ y: -3 }}
      className="snap-start w-44 flex-shrink-0 rounded-2xl bg-white overflow-hidden cursor-pointer"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
    >
      <div className="relative h-32 bg-gray-50">
        {/* Discount badge */}
        <div className="absolute top-2 left-2 z-10 flex items-center gap-0.5 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">
          <Percent size={8} /> {discount}% OFF
        </div>

        {product.images?.[0]
          ? <img src={getImageUrl(product.images[0])} alt={product.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <Icons.Package size={28} className="text-gray-300" />
            </div>
        }

        {/* Company name gradient overlay */}
        {showTitle && (
          <>
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute bottom-1.5 left-2 right-2 text-[9px] text-white font-semibold truncate z-10">
              {product.company?.name}
            </p>
          </>
        )}
      </div>

      <div className="p-2.5">
        <p className="text-[11px] text-gray-800 font-semibold line-clamp-2 leading-snug mb-1.5">{product.title}</p>
        <div className="flex items-baseline gap-1.5">
          <p className="text-[10px] text-gray-400 line-through">${Number(product.price).toLocaleString("es-CO")}</p>
          <p className="text-sm font-black text-supernova-600">${Number(product.offerPrice).toLocaleString("es-CO")}</p>
        </div>
        <p className="text-[10px] text-green-600 font-bold mt-0.5">
          Ahorras ${Number(product.price - product.offerPrice).toLocaleString("es-CO")}
        </p>

        <div className="flex items-center gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
          {onQuickView && (
            <button
              type="button"
              onClick={() => onQuickView(product)}
              className="p-1.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
              title="Ver descripción"
            >
              <Eye size={11} className="text-gray-400" />
            </button>
          )}
          <CartQtyControl product={product} isOpen={isOpen} size="sm" addClassName="bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm" />
        </div>
      </div>
    </motion.div>
  );
}

// â"€â"€ Product card (grid) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function ProductCard({ product, isOpen, closedMessage, isFavorite, onFavorite, onClick, onQuickView }) {
  const discount = product.offerPrice
    ? Math.round(((product.price - product.offerPrice) / product.price) * 100)
    : null;

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ y: -3 }}
      className="bg-white rounded-2xl overflow-hidden cursor-pointer flex flex-col transition-shadow"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
    >
      {/* Image */}
      <div className="relative w-full h-28 bg-gray-50 shrink-0 overflow-hidden">
        {!isOpen && (
          <div className="absolute inset-0 bg-black/20 z-10" />
        )}
        {!isOpen && (
          <div className="absolute top-2 left-2 z-20 bg-black/60 text-white text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5">
            <Clock size={8} /> {closedMessage}
          </div>
        )}
        {discount && (
          <div className="absolute top-2 right-2 z-20 bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full shadow">
            -{discount}%
          </div>
        )}
        {product.images?.[0]
          ? <img
              src={getImageUrl(product.images[0])}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          : <div className="w-full h-full flex items-center justify-center text-gray-200">
              <Icons.ImageOff size={28} />
            </div>
        }
      </div>

      {/* Info */}
      <div className="p-2 flex flex-col flex-1">
        <p className="text-[10px] font-bold text-gray-800 line-clamp-2 leading-snug mb-1">{product.title}</p>

        {product.offerPrice ? (
          <div className="flex items-baseline gap-1 mb-0.5">
            <span className="text-[9px] text-gray-400 line-through">${Number(product.price).toLocaleString("es-CO")}</span>
            <span className="text-xs font-black text-supernova-600">${Number(product.offerPrice).toLocaleString("es-CO")}</span>
          </div>
        ) : (
          <p className="text-xs font-black text-gray-900 mb-0.5">${Number(product.price).toLocaleString("es-CO")}</p>
        )}

        {product.company?.name && (
          <div className="flex items-center gap-0.5 text-[9px] text-gray-400 mb-1.5 mt-0.5">
            <span className="truncate">{product.company.name}</span>
            {product.company?.verified && <BadgeCheck size={8} className="text-blue-500 shrink-0" />}
          </div>
        )}

        <div className="flex items-center gap-1 mt-auto" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onFavorite}
            className="p-1 rounded-lg border border-gray-100 bg-gray-50 hover:bg-red-50 transition shrink-0"
          >
            <Heart size={11} className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-300"} />
          </button>
          {onQuickView && (
            <button
              type="button"
              onClick={() => onQuickView(product)}
              className="p-1 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
              title="Ver descripción"
            >
              <Eye size={11} className="text-gray-400" />
            </button>
          )}
          <CartQtyControl product={product} isOpen={isOpen} size="sm" addClassName="bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm" />
        </div>
      </div>
    </motion.div>
  );
}

// â"€â"€ Sponsor tier config â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
const SPONSOR_TIERS = [
  {
    id: "gold",
    ids: ["gold", "platinum"],
    label: "Oro",
    size: "w-16 h-16",
    border: "border-yellow-400 ring-2 ring-yellow-200",
    scroll: false,
  },
  {
    id: "silver",
    ids: ["silver"],
    label: "Plata",
    size: "w-12 h-12",
    border: "border-gray-300",
    scroll: false,
  },
  {
    id: "bronze",
    ids: ["bronze"],
    label: "Bronce",
    size: "w-10 h-10",
    border: "border-orange-300",
    scroll: true,
  },
];

// â"€â"€ Participating products for an event â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
function EventProductsList({ event }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const allSponsors = event.sponsors ?? [];

  // Products with company from their sponsor entry
  const products = allSponsors.flatMap((s) =>
    (s.products ?? []).map((sp) => {
      const p = sp.product;
      if (!p) return null;
      return { ...p, _company: p.company ?? s.company };
    }).filter(Boolean)
  );

  const eid = event.id ?? event._id;
  const sectionLabel = event.productsButtonLabel || "Participantes";

  // Group sponsors by tier — exclude participating restaurants (those with products)
  const tieredSponsors = SPONSOR_TIERS.map((tier) => ({
    ...tier,
    sponsors: allSponsors.filter((s) => {
      const t = (s.membershipTier ?? "").toLowerCase();
      const hasProducts = (s.products?.length ?? 0) > 0;
      return tier.ids.includes(t) && !hasProducts && (s.company?.logo || s.company?.logoBase64);
    }),
  })).filter((t) => t.sponsors.length > 0);

  const hasSponsors = tieredSponsors.length > 0;
  if (!products.length && !hasSponsors) return null;

  const openDirections = (company) => {
    if (!company) return;
    // Prefer the first sede's coordinates for the most accurate branch location
    const sede = company.sedes?.[0];
    const lat = sede?.latitude ?? sede?.lat ?? company.locationLat ?? company.latitude;
    const lng = sede?.longitude ?? sede?.lng ?? company.locationLng ?? company.longitude;
    let url;
    if (lat && lng) {
      // Opens turn-by-turn navigation directly to the branch
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    } else if (sede?.address || company.address) {
      const dest = encodeURIComponent(`${company.name} ${sede?.address ?? company.address ?? ""}`.trim());
      url = `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.name ?? "")}`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-3">

      {/* ── Patrocinadores por nivel (carrusel centrado) ── */}
      {hasSponsors && (
        <div className="mb-4 rounded-2xl bg-gray-50 border border-gray-100 py-3 px-0 overflow-hidden">
          <style>{`@keyframes landing-bronze-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
          <p className="text-center text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 px-3">Patrocinadores</p>
          {tieredSponsors.map((tier, ti) => {
            const items = tier.scroll ? [...tier.sponsors, ...tier.sponsors] : tier.sponsors;
            return (
              <div key={tier.id} className={ti > 0 ? "mt-3 pt-3 border-t border-gray-100" : ""}>
                <p className="text-center text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  {tier.id === "gold" ? "🥇" : tier.id === "silver" ? "🥈" : "🥉"} {tier.label}
                </p>
                <div className="overflow-hidden">
                  <div
                    className="flex gap-5 px-3 pb-0.5"
                    style={tier.scroll
                      ? { animation: "landing-bronze-scroll 28s linear infinite", width: "max-content" }
                      : { display: "flex", justifyContent: "center", gap: "1.25rem", paddingLeft: "0.75rem", paddingRight: "0.75rem" }
                    }
                  >
                    {items.map((s, i) => {
                      const logo = getImageUrl(s.company.logo ?? s.company.logoBase64);
                      const cid  = s.company?.id ?? s.company?._id;
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
                          onClick={() => cid && navigate(`/company/${cid}`)}
                          title={s.company.name}
                        >
                          {logo
                            ? <img src={logo} alt={s.company.name} className={`${tier.size} rounded-full object-cover border-2 ${tier.border} shadow transition-transform duration-200 hover:scale-110`} />
                            : <div className={`${tier.size} rounded-full bg-white border-2 ${tier.border} flex items-center justify-center text-gray-300 text-xs transition-transform duration-200 hover:scale-110`}>🏪</div>
                          }
                          <span className="text-[8px] text-gray-500 font-semibold text-center leading-tight" style={{ maxWidth: 56 }}>{s.company.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Productos ── */}
      {products.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2.5">
            <Icons.ShoppingBag size={13} className="text-supernova-500 shrink-0" />
            <span className="text-xs font-black text-gray-700">{sectionLabel}</span>
            <span className="text-[10px] font-bold text-supernova-600 bg-supernova-100 px-1.5 py-0.5 rounded-full">
              {products.length}
            </span>
            <button
              onClick={() => navigate(`/event/${eid}/productos`)}
              className="ml-auto flex items-center gap-1 text-[11px] font-bold text-supernova-600 hover:text-supernova-700 transition-colors"
            >
              Ver más <ChevronRight size={12} />
            </button>
          </div>

          <div className="overflow-x-auto no-scrollbar">
            <div className="flex gap-2.5 w-max pb-1">
              {products.map((product) => {
                const pid      = product.id ?? product._id;
                const company  = product._company;
                const hasStock = product.stock == null || Number(product.stock) > 0;
                const discount = product.offerPrice
                  ? Math.round(((Number(product.price) - Number(product.offerPrice)) / Number(product.price)) * 100)
                  : null;

                return (
                  <div
                    key={pid}
                    className="w-[130px] flex-shrink-0 rounded-2xl bg-white overflow-hidden flex flex-col"
                    style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}
                  >
                    {/* Image */}
                    <div onClick={() => navigate(`/product/${pid}`)} className="relative h-[90px] bg-gray-50 overflow-hidden cursor-pointer">
                      {discount && (
                        <div className="absolute top-1.5 left-1.5 z-10 bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full shadow">
                          -{discount}%
                        </div>
                      )}
                      {product.images?.[0]
                        ? <img src={getImageUrl(product.images[0])} alt={product.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center bg-gray-100"><Icons.ImageOff size={20} className="text-gray-300" /></div>
                      }
                      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>

                    {/* Info */}
                    <div className="p-2 flex flex-col flex-1">
                      <p onClick={() => navigate(`/product/${pid}`)} className="text-[10px] font-bold text-gray-800 line-clamp-2 leading-snug cursor-pointer">
                        {product.title}
                      </p>

                      {company?.name && (
                        <p className="text-[9px] text-gray-400 font-medium truncate mt-0.5 mb-1">{company.name}</p>
                      )}

                      {product.offerPrice ? (
                        <div className="flex items-baseline gap-1 mb-1.5">
                          <span className="text-[8px] text-gray-400 line-through">${Number(product.price).toLocaleString("es-CO")}</span>
                          <span className="text-[11px] font-black text-supernova-600">${Number(product.offerPrice).toLocaleString("es-CO")}</span>
                        </div>
                      ) : (
                        <p className="text-[11px] font-black text-gray-900 mb-1.5">${Number(product.price).toLocaleString("es-CO")}</p>
                      )}

                      {/* Cart + Cómo llegar */}
                      <div className="flex gap-1 mt-auto">
                        <button
                          disabled={!hasStock}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!hasStock) return;
                            addToCart(normalize(product));
                            toast(<CartToast product={product} />, { icon: "🛒", autoClose: 2000 });
                          }}
                          className={`flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-xl text-[10px] font-bold transition
                            ${hasStock ? "bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                        >
                          {hasStock ? <ShoppingCart size={13} /> : <span className="text-[8px]">Agot.</span>}
                        </button>
                        {company && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openDirections(company); }}
                            className="flex items-center justify-center gap-0.5 flex-1 h-8 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100 transition"
                          >
                            <Navigation size={9} className="text-blue-500 flex-shrink-0" />
                            <span className="text-[9px] font-bold text-blue-600">Cómo llegar</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// â"€â"€â"€ Main component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
export default function Landing() {
  const [searchParams] = useSearchParams();
  const navigate    = useNavigate();

  // Redirect legacy /?offers=true â†' /ofertas
  useEffect(() => {
    if (searchParams.get("offers") === "true") {
      navigate("/ofertas", { replace: true });
    }
  // eslint-disable-next-line
  }, []);

  const [activeAuctions, setActiveAuctions] = useState(0);

  useEffect(() => {
    API.get("/auctions?status=active&limit=1")
      .then((res) => setActiveAuctions(res.data?.data?.total ?? 0))
      .catch(() => {});
  }, []);

  const API_IMG_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");
  const getBannerSrc = (bannerBase64) => {
    if (!bannerBase64) return null;
    if (bannerBase64.startsWith("data:") || bannerBase64.startsWith("http")) return bannerBase64;
    return `${API_IMG_BASE}/${bannerBase64.replace(/^\//, "")}`;
  };

  const [events,        setEvents]        = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [companies,     setCompanies]     = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [products,      setProducts]      = useState([]);
  const [offerProducts, setOfferProducts] = useState([]);
  const [quickView,     setQuickView]     = useState(null); // { product, isOpen, closedMessage } | null
  const [landingConfig, setLandingConfig] = useState({
    showEventBanners:     true,
    showDailyOffers:      true,
    showCardTitles:       true,
    sections:             [],
    useBlocks:            false,
    blocks:               [],
    theme:                {},
    productGridCategoryId: null,
  });

  const [locationLabel,   setLocationLabel]   = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [, setLocationCoords]  = useState(null); // { lat, lng }
  const [showLocPicker,   setShowLocPicker]   = useState(false);
  const [locSearch,       setLocSearch]       = useState("");
  const [locResults,      setLocResults]      = useState([]);
  const [locSearching,    setLocSearching]    = useState(false);
  const locPickerRef = useRef(null);

  const [page,    setPage]    = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const limit = 20;

  // undefined = config not yet applied; null = user chose "Todos"; string = specific category
  const [selectedCategory, setSelectedCategory] = useState(undefined);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [searchedCompanies, setSearchedCompanies] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const { search, setSearch } = useSearch();
  const { favorites, toggleFavorite } = useFavorites();

  const sliderRef   = useRef(null);
  const bottomRef   = useRef(null);
  const observerRef = useRef(null);
  const setCurrentSlide = useState(0)[1];

  // â"€â"€ Auth ping â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
    if (!token) return;
    API.get("/auth/me").catch(() => {});
  }, []);

  // â"€â"€ Landing config â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const fetchLandingConfig = useCallback(() => {
    API.get("/settings/landing")
      .then((res) => {
        const cfg = res.data?.data?.config ?? {};
        const productGridCategoryId = cfg.productGridCategoryId ?? null;
        setLandingConfig({
          showEventBanners:     cfg.showEventBanners ?? true,
          showDailyOffers:      cfg.showDailyOffers  ?? true,
          showCardTitles:       cfg.showCardTitles   ?? true,
          sections:             Array.isArray(cfg.sections) ? cfg.sections : [],
          useBlocks:            cfg.useBlocks  ?? false,
          blocks:               Array.isArray(cfg.blocks) ? cfg.blocks : [],
          theme:                cfg.theme ?? {},
          productGridCategoryId,
        });
        // Apply default category only if user hasn't explicitly selected one yet
        setSelectedCategory(prev => prev === undefined ? productGridCategoryId : prev);
      })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLandingConfig();
    // Refetch cuando el tab vuelve a estar activo (ej: admin guardó cambios en otra pestaña)
    const onVisible = () => { if (document.visibilityState === "visible") fetchLandingConfig(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchLandingConfig]);

  // â"€â"€ Ofertas â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    API.get("/products/offers?limit=20")
      .then((res) => {
        const raw = res.data?.data?.products ?? res.data?.products ?? [];
        setOfferProducts((Array.isArray(raw) ? raw : []).map(normalize));
      })
      .catch(() => setOfferProducts([]));
  }, []);


  // â"€â"€ Sede helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const normalizeSedes = (product) => {
    if (Array.isArray(product.sedes)) return product.sedes;
    if (Array.isArray(product.productSedes))
      return product.productSedes.map((ps) => ps.sede ?? ps);
    return [];
  };

  const productHasOpenSede = (product) => {
    const sedes = normalizeSedes(product);
    if (!sedes.length) return false;
    return sedes.some((s) => isSedeOpen(s).open);
  };

  const getClosedSedeMessage = (product) => {
    const sedes = normalizeSedes(product);
    if (!sedes.length) return "Cerrado";
    return isSedeOpen(sedes[0]).message || "Cerrado";
  };

  // â"€â"€ FCM â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  async function initNotifications() {
    try {
      let currentToken = null;
      try { currentToken = await requestPermissionAndToken(); } catch { return; }
      if (!currentToken) return;
      const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
      if (!token) return;
      const { data } = await API.get("/auth/me");
      const user = data?.data?.user ?? data?.user;
      if (!user) return;
      if (!user.fcmToken || user.fcmToken !== currentToken) {
        const today = new Date().toISOString().slice(0, 10);
        if (user.fcmToken && user.fcmToken !== currentToken) {
          const last = localStorage.getItem("fcmPromptDate");
          if (last === today) return;
          localStorage.setItem("fcmPromptDate", today);
          const ok = window.confirm(
            "âš ï¸ Ya tienes notificaciones activas en otro dispositivo.\n\n" +
            "¿Deseas activar las notificaciones en este dispositivo?"
          );
          if (!ok) return;
        }
        await API.post("/notifications/register", { token: currentToken, userId: user.id ?? user._id });
        if (user.fcmToken) toast.success("🔔 Notificaciones activadas en este dispositivo");
      }
    } catch { /* silencioso */ }
  }

  useEffect(() => {
    initNotifications();
    try { listenForegroundMessages(); } catch {}
    // eslint-disable-next-line
  }, []);

  // â"€â"€ Events auto-scroll â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    if (!events.length) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % events.length;
        if (sliderRef.current)
          sliderRef.current.scrollTo({ left: sliderRef.current.clientWidth * next, behavior: "smooth" });
        return next;
      });
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // â"€â"€ Fetch initial data â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    API.get("/events?active=true")
      .then((res) => {
        const raw = res.data?.data?.events ?? res.data?.events ?? res.data ?? [];
        const now = new Date();
        const current = Array.isArray(raw)
          ? raw.map(normalize).filter((ev) => !ev.endDate || new Date(ev.endDate) >= now)
          : [];
        setEvents(current);
      })
      .catch(() => setEvents([]));

    API.get("/categories")
      .then((res) => {
        const raw = res.data?.data?.categories ?? res.data?.categories ?? res.data ?? [];
        setCategories(Array.isArray(raw) ? raw.map(normalize) : []);
      })
      .catch(() => {});

    const fetchCompanies = async (lat, lng) => {
      const url = lat && lng ? `/companies/public?lat=${lat}&lng=${lng}` : `/companies/public`;
      const res = await API.get(url);
      const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      const list = Array.isArray(raw) ? raw.map(normalize) : [];
      // Verified first, then random within each group
      return sortVerifiedFirst(list);
    };

    const reverseGeocode = async (lat, lng) => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`
        );
        const d = await r.json();
        return d.address?.city || d.address?.town || d.address?.village || d.address?.county || "Ubicación activa";
      } catch {
        return "Ubicación activa";
      }
    };

    // El GPS es una mejora progresiva (ordena por cercanía), no un requisito para ver
    // la página — antes se esperaba a navigator.geolocation.getCurrentPosition() antes
    // de pedir los comercios, lo que podía demorar varios segundos (permiso del
    // navegador, GPS frío, o el timeout completo de 8s si el usuario no respondía al
    // prompt) con la página en blanco mientras tanto. Ahora se pide la lista YA, sin
    // ubicación, y si el GPS resuelve después, se vuelve a pedir con lat/lng para
    // reordenar por cercanía — sin bloquear el primer render.
    const loadCompanies = async () => {
      try { setCompanies(await fetchCompanies()); } catch {}
      setCompaniesLoaded(true);

      if (!navigator.geolocation) return;
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude: lat, longitude: lng } = pos.coords;
            setLocationCoords({ lat, lng });
            setCompanies(await fetchCompanies(lat, lng));
            setLocationLabel(await reverseGeocode(lat, lng));
          } catch {}
          finally { setLocationLoading(false); }
        },
        () => { setLocationLoading(false); },
        { timeout: 8000 }
      );
    };

    loadCompanies();
    // eslint-disable-next-line
  }, []);

  // â"€â"€ Reset products on filter change â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
  }, [selectedCategory, search]);

  // â"€â"€ Server-side company search/filter (debounced) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  useEffect(() => {
    const q = search.trim();
    if (!q && !selectedCategory) { setSearchedCompanies([]); return; }
    setSearchingCompanies(true);
    const params = new URLSearchParams({ limit: "100" });
    if (q) params.set("search", q);
    if (selectedCategory) params.set("categoryId", selectedCategory);
    const timer = setTimeout(async () => {
      try {
        const res = await API.get(`/companies/public?${params}`);
        const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
        setSearchedCompanies(Array.isArray(raw) ? raw.map(normalize) : []);
      } catch {
        setSearchedCompanies([]);
      } finally {
        setSearchingCompanies(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [search, selectedCategory]);

  // â"€â"€ Infinite scroll products â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const loadMore = useCallback(
    async (currentPage = page) => {
      if (loading || !hasMore) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(currentPage), limit: String(limit) });
        if (search.trim()) params.set("search", search.trim());
        if (selectedCategory) params.set("categoryId", selectedCategory);
        const res = await API.get(`/products?${params}`);
        const raw = res.data?.data?.data ?? res.data?.data ?? res.data?.products ?? res.data ?? [];
        const incoming = shuffle((Array.isArray(raw) ? raw : []).map(normalize));
        setProducts((prev) => {
          const ids = new Set(prev.map((p) => p._id));
          const merged = [...prev];
          for (const p of incoming) {
            if (!ids.has(p._id)) { merged.push(p); ids.add(p._id); }
          }
          return merged;
        });
        const pages = res.data?.data?.pages ?? res.data?.pages;
        setHasMore(pages ? currentPage < pages : incoming.length === limit);
        setPage((prev) => prev + 1);
      } catch (err) {
        console.error("Error cargando productos:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, hasMore, loading, search, selectedCategory]
  );

  useEffect(() => {
    if (!configLoaded) return;
    if (page === 1 && products.length === 0) loadMore(1);
    // eslint-disable-next-line
  }, [products.length, page, loadMore, configLoaded]);

  useEffect(() => {
    if (!bottomRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasMore && !loading) loadMore(); },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    observerRef.current.observe(bottomRef.current);
    return () => { if (observerRef.current) observerRef.current.disconnect(); };
    // eslint-disable-next-line
  }, [bottomRef.current, hasMore, loading, loadMore]);

  // â"€â"€ Derived data â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const validOfferProducts = offerProducts.filter(
    (p) => p.offerPrice && p.price && Number(p.offerPrice) < Number(p.price) && normalizeSedes(p).length > 0
  );

  const filteredCompanies = companies.filter((c) => {
    const matchesCategory = !selectedCategory ||
      c.categoryId === selectedCategory ||
      c.category?.id === selectedCategory;
    const q = search.trim().toLowerCase();
    const matchesSearch = !q ||
      c.name?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const visibleSections = landingConfig.sections
    .filter((s) => s.visible)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => {
      const sectionCompanies = filteredCompanies.filter((c) =>
        c.categoryId === s.categoryId || c.category?.id === s.categoryId
      );
      const companyIds = new Set(sectionCompanies.map((c) => c._id));
      const sectionOffers = s.showOffers
        ? validOfferProducts.filter((p) => {
            const cId = p.companyId ?? p.company?._id ?? p.company?.id;
            return companyIds.has(cId);
          })
        : [];
      return { ...s, companies: sectionCompanies, offers: sectionOffers };
    });

  const sectionCategoryIds = new Set(landingConfig.sections.map((s) => s.categoryId));
  const otherCompanies = filteredCompanies.filter(
    (c) => !sectionCategoryIds.has(c.categoryId) && !sectionCategoryIds.has(c.category?.id)
  );

  const selectedCategoryData = selectedCategory
    ? categories.find((c) => c._id === selectedCategory)
    : null;

  const isFiltered = Boolean(search.trim()) || Boolean(selectedCategory);
  const isTextSearch = Boolean(search.trim());
  const displayCompanies = isFiltered ? searchedCompanies : filteredCompanies;
  const companiesResultLoading = isFiltered ? searchingCompanies : !companiesLoaded;
  const displayCompanyIds = new Set(displayCompanies.map((c) => c._id));

  const filteredProducts = products.filter((p) => {
    if (isFiltered && displayCompanies.length > 0) {
      const pCompanyId = p.companyId ?? p.company?._id ?? p.company?.id;
      if (!displayCompanyIds.has(pCompanyId)) return false;
    }
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      p.title?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.company?.name?.toLowerCase().includes(q)
    );
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aOpen = productHasOpenSede(a);
    const bOpen = productHasOpenSede(b);
    return aOpen === bOpen ? 0 : aOpen ? -1 : 1;
  });

  // â"€â"€ Location picker helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const searchLocation = async (q) => {
    if (!q.trim()) { setLocResults([]); return; }
    setLocSearching(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5&accept-language=es`
      );
      const data = await r.json();
      setLocResults(data);
    } catch {
      setLocResults([]);
    } finally {
      setLocSearching(false);
    }
  };

  const selectLocation = async (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setLocationCoords({ lat, lng });
    setLocationLabel(result.display_name.split(",")[0]);
    setShowLocPicker(false);
    setLocSearch("");
    setLocResults([]);
    setCompaniesLoaded(false);
    try {
      const url = `/companies/public?lat=${lat}&lng=${lng}`;
      const res = await API.get(url);
      const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(raw) ? raw.map(normalize) : []);
    } catch {}
    setCompaniesLoaded(true);
  };

  const useGPSLocation = () => {
    setShowLocPicker(false);
    setLocationLabel(null);
    setLocationLoading(true);
    setCompaniesLoaded(false);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setLocationCoords({ lat, lng });
        try {
          const url = `/companies/public?lat=${lat}&lng=${lng}`;
          const res = await API.get(url);
          const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
          setCompanies(Array.isArray(raw) ? raw.map(normalize) : []);
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`
          );
          const d = await r.json();
          setLocationLabel(d.address?.city || d.address?.town || d.address?.village || "Ubicación GPS");
        } catch {
          setLocationLabel("Ubicación GPS");
        }
        setCompaniesLoaded(true);
        setLocationLoading(false);
      },
      () => { setLocationLoading(false); setCompaniesLoaded(true); },
      { timeout: 8000 }
    );
  };

  // Close picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (locPickerRef.current && !locPickerRef.current.contains(e.target)) {
        setShowLocPicker(false);
      }
    };
    if (showLocPicker) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLocPicker]);

  const handleToggleFavorite = (e, product) => {
    e.stopPropagation();
    toggleFavorite(product);
    const exists = favorites.some((f) => f._id === product._id);
    toast.info(
      !exists ? `${product.title} agregado a favoritos â¤ï¸` : `${product.title} quitado de favoritos`,
      { autoClose: 1200 }
    );
  };

  // â"€â"€â"€ Block renderer â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const BANNER_HEIGHTS = { sm: "h-32", md: "h-44 sm:h-56", lg: "h-64 sm:h-72 md:h-80", xl: "h-80 sm:h-96" };
  const TEXT_SIZES     = { sm: "text-sm", base: "text-base", lg: "text-lg", xl: "text-xl", "2xl": "text-2xl", "3xl": "text-3xl", "4xl": "text-4xl" };
  const TEXT_WEIGHTS   = { normal: "font-normal", medium: "font-medium", semibold: "font-semibold", bold: "font-bold", black: "font-black" };
  const TEXT_PADDING   = { none: "", sm: "px-4 py-2", md: "px-4 py-4", lg: "px-4 py-8" };
  const DIV_SPACING    = { sm: "my-2", md: "my-4", lg: "my-8", xl: "my-12" };
  const SPACER_H       = { sm: "h-2", md: "h-4", lg: "h-8", xl: "h-16" };
  const IMG_HEIGHTS    = { sm: "h-[120px]", md: "h-[180px]", lg: "h-[240px]", xl: "h-[320px]" };
  const RADII          = { none: "", md: "rounded-xl", xl: "rounded-2xl", "2xl": "rounded-3xl", "3xl": "rounded-[28px]", full: "rounded-full" };

  const renderBlock = (block) => {
    if (!block.visible) return null;
    const cfg = block.config ?? {};

    if (block.type === "events_hero") {
      if (!events.length) return null;
      const hClass = BANNER_HEIGHTS[cfg.height] ?? BANNER_HEIGHTS.md;
      return (
        <section key={block.id} className="pt-4 pb-2 px-4">
          <div ref={sliderRef} className="overflow-x-auto no-scrollbar flex snap-x snap-mandatory gap-3 md:gap-4">
            {events.map((ev) => {
              const bannerSrc = getBannerSrc(ev.bannerBase64);
              return (
                <div key={ev._id} className="flex-shrink-0 w-[90vw] sm:w-[580px] md:w-[680px] lg:w-[820px] snap-center">
                  <div
                    onClick={() => navigate(`/event/${ev._id}`)}
                    className={`relative w-full ${hClass} rounded-3xl overflow-hidden cursor-pointer`}
                    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
                  >
                    {bannerSrc
                      ? <img src={bannerSrc} alt={ev.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-gradient-to-br from-supernova-400 to-supernova-600 flex items-center justify-center"><span className="text-white text-4xl">🏆</span></div>
                    }
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-4 left-5 text-white">
                      <p className="font-black text-base drop-shadow-md">{ev.title}</p>
                      <p className="text-white/70 text-xs mt-0.5">Toca para más info</p>
                    </div>
                  </div>
                  <EventProductsList event={ev} />
                </div>
              );
            })}
          </div>
        </section>
      );
    }

    if (block.type === "auctions_banner") {
      if (!activeAuctions) return null;
      return (
        <div key={block.id} className="px-4 pt-4">
          <Link to="/auctions">
            <motion.div initial={{ opacity:0,y:-6 }} animate={{ opacity:1,y:0 }}
              className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-3 cursor-pointer hover:bg-gray-800 transition-colors"
              style={{ boxShadow:"0 4px 16px rgba(0,0,0,0.2)" }}>
              <div className="flex items-center justify-center w-9 h-9 bg-supernova-500 rounded-xl flex-shrink-0"><Gavel size={18} className="text-black" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-white text-sm leading-tight flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  {activeAuctions} subasta{activeAuctions !== 1 ? "s" : ""} en vivo
                </p>
                <p className="text-gray-400 text-[11px] mt-0.5">Puja en tiempo real Â· ¡No te las pierdas!</p>
              </div>
              <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
            </motion.div>
          </Link>
        </div>
      );
    }

    if (block.type === "category_section") {
      const sectionCompanies = filteredCompanies.filter((c) =>
        c.categoryId === cfg.categoryId || c.category?.id === cfg.categoryId
      );
      const companyIds = new Set(sectionCompanies.map((c) => c._id));
      const sectionOffers = cfg.showOffers
        ? validOfferProducts.filter((p) => {
            const cId = p.companyId ?? p.company?._id ?? p.company?.id;
            return companyIds.has(cId);
          })
        : [];
      return (
        <React.Fragment key={block.id}>
          <section className="pt-6 pb-1">
            <SectionHeader
              title={`${cfg.emoji ?? "🏪"} ${cfg.label ?? "Tiendas"}`}
              onSeeAll={() => navigate(`/companies?categoryId=${cfg.categoryId}`)}
              count={companiesLoaded ? sectionCompanies.length : null}
            />
            <div className="overflow-x-auto no-scrollbar md:overflow-visible">
              <div className="flex gap-4 pb-3 px-4 w-max md:w-auto md:flex-wrap">
                {!companiesLoaded
                  ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} wide />)
                  : sectionCompanies.length > 0
                    ? sectionCompanies.map((c, i) => (
                        <RestaurantCard key={c._id} company={c} index={i} onClick={() => navigate(`/company/${c._id}`)} showTitle={landingConfig.showCardTitles} />
                      ))
                    : <div className="flex items-center gap-3 px-2 py-4 text-sm text-gray-400"><Icons.Store size={18} className="text-gray-300" /> No hay tiendas disponibles</div>
                }
              </div>
            </div>
          </section>
          {sectionOffers.length > 0 && (
            <section className="pt-2 pb-1">
              <div className="mx-4 mb-4 rounded-2xl overflow-hidden bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 px-5 py-4 flex items-center justify-between" style={{ boxShadow:"0 6px 24px rgba(239,68,68,0.3)" }}>
                <div>
                  <div className="flex items-center gap-2 mb-0.5"><span className="text-xl">{cfg.emoji ?? "🏪"}</span><p className="text-white font-black text-base leading-tight">Promos en {cfg.label ?? "Tiendas"}</p></div>
                  <p className="text-white/75 text-[11px]">Descuentos exclusivos Â· hoy</p>
                </div>
                <div className="bg-white/20 rounded-xl px-3 py-1.5 text-center">
                  <p className="text-white font-black text-lg leading-none">{sectionOffers.length}</p>
                  <p className="text-white/80 text-[10px] font-semibold">promo{sectionOffers.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="overflow-x-auto no-scrollbar">
                <div className="flex gap-3 pb-3 px-4 w-max">
                  {sectionOffers.map((p) => {
                    const isOpen = productHasOpenSede(p);
                    const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                    return (
                      <OfferCard key={p._id} product={p} showTitle={landingConfig.showCardTitles} isOpen={isOpen}
                        onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                        onQuickView={() => setQuickView({ product: p, isOpen, closedMessage: !isOpen ? getClosedSedeMessage(p) : null })}
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </React.Fragment>
      );
    }

    if (block.type === "offers_section") {
      if (!validOfferProducts.length) return null;
      const limit = cfg.limit ?? 20;
      const shown = validOfferProducts.slice(0, limit);
      return (
        <section key={block.id} className="pt-3 pb-1">
          <div className="mx-4 mb-3 rounded-2xl overflow-hidden relative"
            style={{ background:"linear-gradient(135deg,#ff4500 0%,#ff6b35 50%,#ffa726 100%)", boxShadow:"0 4px 16px rgba(255,69,0,0.28)" }}>
            <div className="relative px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">🔥</span>
                <div>
                  <p className="text-white font-black text-sm leading-tight drop-shadow">{cfg.title ?? "🔥 Descuentos HOY"}</p>
                  <p className="text-white/75 text-[10px]">{shown.length} producto{shown.length !== 1 ? "s" : ""} en oferta</p>
                </div>
              </div>
              <button onClick={() => navigate("/ofertas")} className="flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold px-3 py-1.5 rounded-full transition border border-white/30">
                Ver más <ChevronRight size={10} />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto no-scrollbar md:overflow-visible">
            <div className="flex gap-3 pb-3 px-4 w-max md:w-auto md:flex-wrap">
              {shown.map((p) => {
                const isOpen = productHasOpenSede(p);
                const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                return (
                  <OfferCard key={p._id} product={p} showTitle={landingConfig.showCardTitles} isOpen={isOpen}
                    onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                    onQuickView={() => setQuickView({ product: p, isOpen, closedMessage: !isOpen ? getClosedSedeMessage(p) : null })}
                  />
                );
              })}
            </div>
          </div>
        </section>
      );
    }

    if (block.type === "stores_section") {
      if (!companiesLoaded && !otherCompanies.length) return null;
      return (
        <section key={block.id} className="pt-6 pb-1">
          <SectionHeader title={cfg.title ?? "🏪 Más tiendas"} onSeeAll={() => navigate("/companies")} count={companiesLoaded ? otherCompanies.length : null} />
          <div className="overflow-x-auto no-scrollbar md:overflow-visible">
            <div className="flex gap-3 pb-3 px-4 w-max md:w-auto md:flex-wrap">
              {!companiesLoaded
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                : otherCompanies.map((c, i) => (
                    <StoreCard key={c._id} company={c} index={i} onClick={() => navigate(`/company/${c._id}`)} showTitle={landingConfig.showCardTitles} />
                  ))
              }
            </div>
          </div>
        </section>
      );
    }

    if (block.type === "products_grid") {
      return (
        <section key={block.id} className="px-4 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-5 bg-supernova-500 rounded-full shrink-0" />
              <h2 className="text-base font-black text-gray-900 tracking-tight">Todos los productos</h2>
            </div>
            {sortedProducts.length > 0 && <span className="text-[11px] text-gray-400 font-bold bg-gray-100 px-2.5 py-1 rounded-full">{sortedProducts.length}</span>}
          </div>
          <AnimatePresence mode="wait">
            {sortedProducts.length > 0 ? (
              <motion.div key="grid" initial={{ opacity:0 }} animate={{ opacity:1 }} className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {sortedProducts.map((p) => {
                  const isOpen = productHasOpenSede(p);
                  const closedMessage = !isOpen ? getClosedSedeMessage(p) : null;
                  const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                  const isFav = favorites.some((f) => f._id === p._id);
                  return (
                    <ProductCard key={p._id} product={p} isOpen={isOpen} closedMessage={closedMessage} isFavorite={isFav}
                      onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                      onFavorite={(e) => handleToggleFavorite(e, p)}
                      onQuickView={() => setQuickView({ product: p, isOpen, closedMessage })}
                    />
                  );
                })}
              </motion.div>
            ) : loading ? (
              <motion.div key="skeleton" className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse" style={{ boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
                    <div className="h-28 bg-gray-100" />
                    <div className="p-3 space-y-2"><div className="h-3 bg-gray-100 rounded-full w-4/5" /><div className="h-4 bg-gray-100 rounded-full w-2/5" /><div className="h-7 bg-gray-100 rounded-xl mt-3" /></div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity:0 }} animate={{ opacity:1 }} className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-4"><Icons.PackageSearch size={36} className="text-gray-300" /></div>
                <p className="text-sm font-bold text-gray-500">No hay productos disponibles</p>
              </motion.div>
            )}
          </AnimatePresence>
          <div ref={bottomRef} className="w-full h-16 mt-4 flex justify-center items-center">
            {loading && sortedProducts.length > 0 && (
              <div className="flex items-center gap-2 text-gray-400 text-sm"><div className="w-4 h-4 border-2 border-supernova-400 border-t-transparent rounded-full animate-spin" /> Cargando más...</div>
            )}
            {!loading && !hasMore && sortedProducts.length > 0 && <span className="text-gray-400 text-xs font-medium">âœ" Viste todo lo disponible</span>}
            {!loading && hasMore && sortedProducts.length > 0 && (
              <button onClick={() => loadMore(page)} className="px-5 py-2 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50 transition shadow-sm">Cargar más</button>
            )}
          </div>
        </section>
      );
    }

    if (block.type === "text_block") {
      const paddingClass = TEXT_PADDING[cfg.padding] ?? "px-4 py-4";
      const sizeClass    = TEXT_SIZES[cfg.fontSize]   ?? "text-xl";
      const weightClass  = TEXT_WEIGHTS[cfg.fontWeight] ?? "font-bold";
      const alignClass   = cfg.align === "center" ? "text-center" : cfg.align === "right" ? "text-right" : "text-left";
      return (
        <div key={block.id} className={`${paddingClass}`}
          style={{ backgroundColor: cfg.backgroundColor === "transparent" ? undefined : cfg.backgroundColor }}>
          <p className={`${sizeClass} ${weightClass} ${alignClass}`} style={{ color: cfg.color ?? "#111827" }}>
            {cfg.text}
          </p>
        </div>
      );
    }

    if (block.type === "banner_image") {
      if (!cfg.imageBase64) return null;
      const hClass    = IMG_HEIGHTS[cfg.height] ?? IMG_HEIGHTS.md;
      const radClass  = RADII[cfg.borderRadius] ?? "rounded-2xl";
      const content   = (
        <div className={`mx-4 overflow-hidden ${radClass} ${hClass}`}>
          <img src={cfg.imageBase64} alt="banner" className="w-full h-full" style={{ objectFit: cfg.objectFit ?? "cover" }} />
        </div>
      );
      return (
        <div key={block.id} className="pt-3">
          {cfg.link
            ? cfg.link.startsWith("http")
              ? <a href={cfg.link} target="_blank" rel="noopener noreferrer">{content}</a>
              : <span onClick={() => navigate(cfg.link)} className="cursor-pointer block">{content}</span>
            : content
          }
        </div>
      );
    }

    if (block.type === "divider") {
      return (
        <div key={block.id} className={`px-4 ${DIV_SPACING[cfg.spacing] ?? "my-4"}`}>
          <hr style={{ borderStyle: cfg.style ?? "solid", borderColor: cfg.color ?? "#e5e7eb", borderWidth: "1px" }} />
        </div>
      );
    }

    if (block.type === "spacer") {
      return <div key={block.id} className={SPACER_H[cfg.height] ?? "h-4"} />;
    }

    return null;
  };

  // â"€â"€â"€ RENDER â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  return (
    <div className="min-h-screen bg-[#f7f8f9] pb-24 lg:pb-8">

      {/* â"€â"€ Sticky header: location + search + categories â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      {categories.length > 0 && (
        <div className="bg-white border-b border-gray-100 sticky top-0 z-30" style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
          {/* Location bar */}
          <div className="relative px-4 pt-2.5 pb-1" ref={locPickerRef}>
            <button
              onClick={() => { setShowLocPicker(!showLocPicker); setLocSearch(""); setLocResults([]); }}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all border
                ${locationLabel
                  ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                  : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                }`}
            >
              {locationLoading
                ? <Loader2 size={11} className="animate-spin" />
                : <Navigation size={11} className={locationLabel ? "text-green-600" : "text-gray-400"} />
              }
              <span className="max-w-[140px] truncate">
                {locationLoading ? "Detectando..." : locationLabel ?? "¿Dónde estás?"}
              </span>
              <ChevronRight size={10} className={`transition-transform ${showLocPicker ? "rotate-90" : ""}`} />
            </button>

            {/* Dropdown picker */}
            {showLocPicker && (
              <div className="absolute top-full left-4 mt-1 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                {/* GPS option */}
                <button
                  onClick={useGPSLocation}
                  className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-100"
                >
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <Navigation size={14} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Usar mi ubicación</p>
                    <p className="text-[10px] text-gray-400">Detectar automáticamente</p>
                  </div>
                </button>

                {/* Search input */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <div className="relative">
                    <Icons.Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Buscar ciudad o zona..."
                      value={locSearch}
                      onChange={(e) => {
                        setLocSearch(e.target.value);
                        searchLocation(e.target.value);
                      }}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-supernova-300"
                    />
                    {locSearching && (
                      <Loader2 size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                    )}
                  </div>
                </div>

                {/* Results */}
                {locResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    {locResults.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => selectLocation(r)}
                        className="w-full flex items-start gap-2.5 px-4 py-2.5 hover:bg-supernova-50 text-left"
                      >
                        <MapPin size={13} className="text-gray-400 shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-700 line-clamp-2">{r.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {locSearch && !locSearching && locResults.length === 0 && (
                  <p className="px-4 py-3 text-xs text-gray-400">No se encontraron resultados</p>
                )}
              </div>
            )}
          </div>
          {/* Search bar */}
          <div className="px-4 pb-2">
            <div className="relative">
              <Icons.Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tiendas, productos, categorías..."
                className="w-full pl-10 pr-9 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-supernova-300 focus:bg-white focus:border-supernova-300 transition-all placeholder:text-gray-400"
              />
              {search ? (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gray-300 hover:bg-gray-400 flex items-center justify-center transition"
                >
                  <Icons.X size={11} className="text-white" />
                </button>
              ) : (
                <Sparkles size={13} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-supernova-400 pointer-events-none" />
              )}
            </div>
          </div>

          <div className="overflow-x-auto no-scrollbar px-4 pb-2.5">
            <div className="flex gap-1.5 w-max">

              {/* "Todos" pill */}
              <button
                onClick={() => setSelectedCategory(null)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
                  !selectedCategory
                    ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                <Icons.LayoutGrid size={12} />
                Todos
              </button>

              {categories.map((cat) => {
                const LucideIcon = Icons[cat.icon];
                const isSelected = selectedCategory === cat._id;
                return (
                  <button
                    key={cat._id}
                    onClick={() => setSelectedCategory(isSelected ? null : cat._id)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
                      isSelected
                        ? "bg-supernova-500 text-white border-supernova-500 shadow-sm shadow-supernova-200"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {LucideIcon
                      ? <LucideIcon size={12} />
                      : <span className="text-sm leading-none">{cat.icon}</span>
                    }
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">

        {/* â"€â"€ Block mode renderer â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {landingConfig.useBlocks && !isFiltered && (
          <>
            {(landingConfig.blocks ?? []).filter(b => b.visible).map(renderBlock)}
          </>
        )}

        {/* â"€â"€ Classic mode â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {(!landingConfig.useBlocks || isFiltered) && <>

        {/* â"€â"€ Subastas activas banner â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {!isFiltered && activeAuctions > 0 && (
          <div className="px-4 pt-4">
            <Link to="/auctions">
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-3 cursor-pointer hover:bg-gray-800 transition-colors"
                style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}
              >
                <div className="flex items-center justify-center w-9 h-9 bg-supernova-500 rounded-xl flex-shrink-0">
                  <Gavel size={18} className="text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-white text-sm leading-tight flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                    {activeAuctions} subasta{activeAuctions !== 1 ? "s" : ""} en vivo ahora
                  </p>
                  <p className="text-gray-400 text-[11px] mt-0.5">Puja en tiempo real Â· ¡No te las pierdas!</p>
                </div>
                <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
              </motion.div>
            </Link>
          </div>
        )}

        {/* â"€â"€ Hero banners â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {!isFiltered && events.length > 0 && landingConfig.showEventBanners && (
          <section className="pt-4 pb-2 px-4">
            <div ref={sliderRef} className="overflow-x-auto no-scrollbar flex snap-x snap-mandatory gap-3 md:gap-4">
              {events.map((ev) => {
                const bannerSrc = getBannerSrc(ev.bannerBase64);
                return (
                <div key={ev._id} className="flex-shrink-0 w-[90vw] sm:w-[580px] md:w-[680px] lg:w-[820px] snap-center">
                  <div
                    onClick={() => navigate(`/event/${ev._id}`)}
                    className="relative w-full h-44 sm:h-56 md:h-64 lg:h-72 rounded-3xl overflow-hidden cursor-pointer"
                    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
                  >
                    {bannerSrc ? (
                      <img src={bannerSrc} alt={ev.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-supernova-400 to-supernova-600 flex items-center justify-center">
                        <span className="text-white text-4xl">🏆</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-4 left-5 text-white">
                      <p className="font-black text-base drop-shadow-md">{ev.title}</p>
                      <p className="text-white/70 text-xs mt-0.5">Toca para más info</p>
                    </div>
                  </div>
                  <EventProductsList event={ev} />
                </div>
              );
              })}
            </div>
          </section>
        )}

        {/* â"€â"€ Search / Category results mode â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        {isFiltered ? (
          <section className="pt-5 pb-2 px-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2.5 mb-0.5">
                  <div className="w-1 h-5 bg-supernova-500 rounded-full shrink-0" />
                  <h2 className="text-base font-black text-gray-900 tracking-tight">
                    {isTextSearch
                      ? `"${search.trim()}"`
                      : selectedCategoryData?.name ?? "Categoría"}
                  </h2>
                </div>
                {!companiesResultLoading && (
                  <p className="text-xs text-gray-400 ml-3.5">
                    {displayCompanies.length} tienda{displayCompanies.length !== 1 ? "s" : ""} encontrada{displayCompanies.length !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
              <button
                onClick={() => { setSearch(""); setSelectedCategory(null); }}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-full border border-gray-200 hover:border-gray-300 bg-white transition"
              >
                <Icons.X size={11} /> Limpiar
              </button>
            </div>

            {companiesResultLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-2xl bg-white overflow-hidden animate-pulse" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                    <div className="h-32 bg-gray-100" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-gray-100 rounded-full w-4/5" />
                      <div className="h-2.5 bg-gray-100 rounded-full w-3/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : displayCompanies.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
              >
                {displayCompanies.map((c, i) => {
                  const isOpen = Array.isArray(c.sedes) && c.sedes.some((s) => isSedeOpen(s).open);
                  return (
                    <motion.div
                      key={c._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      onClick={() => navigate(`/company/${c._id}`)}
                      className="cursor-pointer bg-white rounded-2xl overflow-hidden hover:shadow-md transition"
                      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
                    >
                      <div className="relative h-32 bg-gray-100 overflow-hidden">
                        {c.logo
                          ? <img src={getImageUrl(c.logo)} alt={c.name} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                          : <div className="w-full h-full bg-supernova-50 flex items-center justify-center">
                              <Store size={32} className="text-supernova-200" />
                            </div>
                        }
                        {!isOpen && <div className="absolute inset-0 bg-black/20" />}
                        <div className={`absolute top-2 right-2 flex items-center gap-0.5 text-[9px] font-bold px-2 py-0.5 rounded-full shadow
                          ${isOpen ? "bg-green-500 text-white" : "bg-white/90 text-gray-600"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-white" : "bg-gray-400"}`} />
                          {isOpen ? "Abierto" : "Cerrado"}
                        </div>
                        {c.category?.name && (
                          <div className="absolute bottom-2 left-2 bg-black/40 backdrop-blur-sm text-white text-[9px] font-semibold px-2 py-0.5 rounded-full">
                            {c.category.name}
                          </div>
                        )}
                      </div>
                      <div className="px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          <p className="font-black text-sm text-gray-900 truncate">{c.name}</p>
                          {c.verified && <BadgeCheck size={12} className="text-blue-500 shrink-0" />}
                        </div>
                        {c.description && (
                          <p className="text-[10px] text-gray-400 truncate mt-0.5">{c.description}</p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <div className="text-center py-12 text-gray-300">
                <Store size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm text-gray-400">No se encontraron tiendas</p>
              </div>
            )}
          </section>
        ) : (
          <>
            {/* â"€â"€ Dynamic category sections â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
            {visibleSections.map((section) => (
              <React.Fragment key={section.categoryId}>

                {/* Companies carousel */}
                <section className="pt-6 pb-1">
                  <SectionHeader
                    title={`${section.emoji} ${section.label}`}
                    onSeeAll={() => navigate(`/companies?categoryId=${section.categoryId}`)}
                    count={companiesLoaded ? section.companies.length : null}
                  />
                  <div className="overflow-x-auto no-scrollbar md:overflow-visible">
                    <div className="flex gap-4 pb-3 px-4 w-max md:w-auto md:flex-wrap">
                      {!companiesLoaded
                        ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} wide />)
                        : section.companies.length > 0
                          ? section.companies.map((c, i) => (
                              <RestaurantCard key={c._id} company={c} index={i} onClick={() => navigate(`/company/${c._id}`)} showTitle={landingConfig.showCardTitles} />
                            ))
                          : (
                            <div className="flex items-center gap-3 px-2 py-4 text-sm text-gray-400">
                              <Icons.Store size={18} className="text-gray-300" />
                              No hay tiendas de esta categoría disponibles
                            </div>
                          )
                      }
                    </div>
                  </div>
                </section>

                {/* Offers carousel for this section */}
                {section.offers.length > 0 && (
                  <section className="pt-2 pb-1">
                    <div className="mx-4 mb-4 rounded-2xl overflow-hidden bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 px-5 py-4 flex items-center justify-between"
                      style={{ boxShadow: "0 6px 24px rgba(239,68,68,0.3)" }}>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xl">{section.emoji}</span>
                          <p className="text-white font-black text-base leading-tight">Promos en {section.label}</p>
                        </div>
                        <p className="text-white/75 text-[11px]">Descuentos exclusivos Â· hoy</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-white/20 rounded-xl px-3 py-1.5 text-center">
                          <p className="text-white font-black text-lg leading-none">{section.offers.length}</p>
                          <p className="text-white/80 text-[10px] font-semibold">promo{section.offers.length !== 1 ? "s" : ""}</p>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto no-scrollbar md:overflow-visible">
                      <div className="flex gap-3 pb-3 px-4 w-max md:w-auto md:flex-wrap">
                        {section.offers.map((p) => {
                          const isOpen = productHasOpenSede(p);
                          const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                          return (
                            <motion.div
                              key={p._id}
                              whileHover={{ y: -3 }}
                              onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                              className="snap-start w-48 flex-shrink-0 rounded-2xl bg-white overflow-hidden cursor-pointer"
                              style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
                            >
                              <div className="relative h-32 bg-gray-50">
                                <span className="absolute top-2 left-2 z-10 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow">
                                  -{Math.round(((p.price - p.offerPrice) / p.price) * 100)}% OFF
                                </span>
                                {!isOpen && (
                                  <span className="absolute top-2 right-2 z-10 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                    <Clock size={8} /> Cerrado
                                  </span>
                                )}
                                {p.images?.[0]
                                  ? <img src={getImageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center bg-orange-50">
                                      <Icons.Tag size={28} className="text-orange-200" />
                                    </div>
                                }
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                                  <p className="text-white text-[9px] font-semibold truncate">{p.company?.name}</p>
                                </div>
                              </div>
                              <div className="p-2.5">
                                <p className="text-[11px] text-gray-800 font-semibold line-clamp-2 leading-snug mb-1.5">{p.title}</p>
                                <div className="flex items-baseline gap-1.5">
                                  <p className="text-[10px] text-gray-400 line-through">${Number(p.price).toLocaleString("es-CO")}</p>
                                  <p className="text-sm font-black text-orange-500">${Number(p.offerPrice).toLocaleString("es-CO")}</p>
                                </div>
                                <p className="text-[10px] text-green-600 font-bold mt-0.5">
                                  Ahorra ${Number(p.price - p.offerPrice).toLocaleString("es-CO")}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                )}
              </React.Fragment>
            ))}

            {/* â"€â"€ Descuentos â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
            {validOfferProducts.length > 0 && landingConfig.showDailyOffers && (
              <section className="pt-3 pb-1">
                <div className="mx-4 mb-3 rounded-2xl overflow-hidden relative"
                  style={{ background: "linear-gradient(135deg, #ff4500 0%, #ff6b35 50%, #ffa726 100%)", boxShadow: "0 4px 16px rgba(255,69,0,0.28)" }}>
                  <div className="relative px-4 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🔥</span>
                      <div>
                        <p className="text-white font-black text-sm leading-tight drop-shadow">Descuentos HOY</p>
                        <p className="text-white/75 text-[10px]">{validOfferProducts.length} producto{validOfferProducts.length !== 1 ? "s" : ""} en oferta</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/ofertas")}
                      className="flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold px-3 py-1.5 rounded-full transition-all border border-white/30"
                    >
                      Ver más <ChevronRight size={10} />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto no-scrollbar md:overflow-visible">
                  <div className="flex gap-3 pb-3 px-4 w-max md:w-auto md:flex-wrap">
                    {validOfferProducts.map((p) => {
                      const isOpen = productHasOpenSede(p);
                      const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                      return (
                        <OfferCard
                          key={p._id}
                          product={p}
                          showTitle={landingConfig.showCardTitles}
                          isOpen={isOpen}
                          onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                          onQuickView={() => setQuickView({ product: p, isOpen, closedMessage: !isOpen ? getClosedSedeMessage(p) : null })}
                        />
                      );
                    })}
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      onClick={() => navigate("/ofertas")}
                      className="snap-start w-32 flex-shrink-0 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-2 border-2 border-dashed border-orange-300 bg-orange-50 text-orange-500"
                      style={{ minHeight: "180px" }}
                    >
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <ChevronRight size={20} className="text-orange-500" />
                      </div>
                      <p className="text-[11px] font-black text-center leading-snug px-2">Ver todos<br/>los descuentos</p>
                    </motion.div>
                  </div>
                </div>
              </section>
            )}

            {/* â"€â"€ Otras tiendas â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
            {(companiesLoaded ? otherCompanies.length > 0 : true) && (
              <section className="pt-6 pb-1">
                <SectionHeader
                  title={visibleSections.length > 0 ? "🏪 Más tiendas" : "🏪 Aliados"}
                  onSeeAll={() => navigate("/companies")}
                  count={companiesLoaded ? otherCompanies.length : null}
                />
                <div className="overflow-x-auto no-scrollbar md:overflow-visible">
                  <div className="flex gap-3 pb-3 px-4 w-max md:w-auto md:flex-wrap">
                    {!companiesLoaded
                      ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                      : otherCompanies.map((c, i) => (
                          <StoreCard key={c._id} company={c} index={i} onClick={() => navigate(`/company/${c._id}`)} showTitle={landingConfig.showCardTitles} />
                        ))
                    }
                  </div>
                </div>
              </section>
            )}
          </>
        )}


        {/* â"€â"€ Divider â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        <div className="mx-4 mt-6 mb-2 border-t border-gray-200" />

        {/* â"€â"€ Products grid â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
        <section className="px-4 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-5 bg-supernova-500 rounded-full shrink-0" />
              <h2 className="text-base font-black text-gray-900 tracking-tight">
                {search.trim() ? "Productos encontrados" : selectedCategory ? `Productos Â· ${selectedCategoryData?.name ?? ""}` : "Todos los productos"}
              </h2>
            </div>
            {sortedProducts.length > 0 && (
              <span className="text-[11px] text-gray-400 font-bold bg-gray-100 px-2.5 py-1 rounded-full">
                {sortedProducts.length}
              </span>
            )}
          </div>

          <AnimatePresence mode="wait">
            {sortedProducts.length > 0 ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
              >
                {sortedProducts.map((p) => {
                  const isOpen = productHasOpenSede(p);
                  const closedMessage = !isOpen ? getClosedSedeMessage(p) : null;
                  const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                  const isFav = favorites.some((f) => f._id === p._id);
                  return (
                    <ProductCard
                      key={p._id}
                      product={p}
                      isOpen={isOpen}
                      closedMessage={closedMessage}
                      isFavorite={isFav}
                      onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                      onFavorite={(e) => handleToggleFavorite(e, p)}
                      onQuickView={() => setQuickView({ product: p, isOpen, closedMessage })}
                    />
                  );
                })}
              </motion.div>
            ) : loading ? (
              <motion.div key="skeleton" className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                    <div className="h-28 bg-gray-100" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-gray-100 rounded-full w-4/5" />
                      <div className="h-4 bg-gray-100 rounded-full w-2/5" />
                      <div className="h-7 bg-gray-100 rounded-xl mt-3" />
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <Icons.PackageSearch size={36} className="text-gray-300" />
                </div>
                <p className="text-sm font-bold text-gray-500">No hay productos que coincidan</p>
                <p className="text-xs text-gray-400 mt-1">Prueba con otro filtro o categoría</p>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="mt-4 px-5 py-2 bg-supernova-500 text-white text-xs font-bold rounded-full hover:bg-supernova-600 transition"
                >
                  Ver todos
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Infinite scroll trigger */}
          <div ref={bottomRef} className="w-full h-16 mt-4 flex justify-center items-center">
            {loading && sortedProducts.length > 0 && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-supernova-400 border-t-transparent rounded-full animate-spin" />
                Cargando más...
              </div>
            )}
            {!loading && !hasMore && sortedProducts.length > 0 && (
              <span className="text-gray-400 text-xs font-medium">âœ" Viste todo lo disponible</span>
            )}
            {!loading && hasMore && sortedProducts.length > 0 && (
              <button
                onClick={() => loadMore(page)}
                className="px-5 py-2 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50 transition shadow-sm"
              >
                Cargar más
              </button>
            )}
          </div>
        </section>

        </> /* end classic mode */}

      </div>

      <footer className="mt-12 py-6 text-center text-[11px] text-gray-300 border-t border-gray-100">
        Â© {new Date().getFullYear()} Qkiere.com â€" Todos los derechos reservados
      </footer>

      <MobileBottomBar />

      {quickView && (
        <ProductQuickViewModal
          product={quickView.product}
          isOpen={quickView.isOpen}
          closedMessage={quickView.closedMessage}
          onClose={() => setQuickView(null)}
        />
      )}
    </div>
  );
}
